import { DocumentVersionToAccept, SignedDocumentUrlResponse } from '@eagle/core-data-types';
import { Button, Stack, Typography } from '@ethings/ui-toolkit';
import { Box, Container, Paper, useTheme } from '@mui/material';
import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { PropsWithChildren, Suspense, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth';
import { useConfig, useCountdown, useSmallScreen, useTermsAndConditionsLabel, useTitle } from '../../hooks';
import { ParticleAnimation } from '../../pages';
import { Alert } from '../alert';
import { ErrorBoundary } from '../error-boundary';
import { LoadingButton } from '../loading-button';
import { EthingsLogo } from '../logo';
import { MiddleSpinner } from '../middle-spinner';
import { NormalizedTermsAndConditionsDocument } from './terms-and-conditions.types';
import { normalizeDocuments } from './terms-and-conditions.utils';

interface TermsAndConditionsProps {
  documents: DocumentVersionToAccept[];
  termsToken: string;
  portal: string;
}

type TermsAndConditionsState = {
  name: 'documents';
} | {
  name: 'success';
  accessToken: string;
} | {
  name: 'declined';
}

export const TermsAndConditions: FC<TermsAndConditionsProps> = (props) => {
  return (
    <Suspense fallback={<MiddleSpinner />}>
      <Box sx={{ position: 'relative', height: '100vh', width: '100%', py: 4, overflow: 'auto', zIndex: 1 }}>
        <ErrorBoundary fallback={() => <ErrorAlert portal={props.portal} />}>
          <TermsAndConditionsInner {...props} />
        </ErrorBoundary>
      </Box>
      <ParticleAnimation />
    </Suspense>
  );
};

const TermsAndConditionsInner: FC<TermsAndConditionsProps> = ({ documents, termsToken, portal }) => {
  const [state, setState] = useState<TermsAndConditionsState>({ name: 'documents' });
  const { t, i18n } = useTranslation(['common']);

  const normalizedDocuments = useMemo(() => normalizeDocuments(documents, i18n.resolvedLanguage), [documents, i18n.resolvedLanguage]);

  useTitle(t(`common:common.labels.${portal}`));

  if (normalizedDocuments.length === 0) {
    return <ErrorAlert portal={portal} />;
  }

  switch (state.name) {
    case 'documents':
      return (
        <TermsAndConditionsDocuments
          documents={normalizedDocuments}
          termsToken={termsToken}
          portal={portal}
          onSuccess={(accessToken) => {
            setState({ name: 'success', accessToken });
          }}
          onDecline={() => {
            setState({ name: 'declined' });
          }}
        />
      );

    case 'success':
      return <Success accessToken={state.accessToken} portal={portal} />;

    case 'declined':
      return <Declined portal={portal} />;
  }
};

interface TermsAndConditionsDocumentsProps {
  documents: NormalizedTermsAndConditionsDocument[];
  termsToken: string;
  portal: string;
  onSuccess: (accessToken: string) => void;
  onDecline: () => void;
}

const TermsAndConditionsDocuments: FC<TermsAndConditionsDocumentsProps> = ({ documents, termsToken, portal, onDecline, onSuccess }) => {
  const { t } = useTranslation(['common', 'auth']);
  const config = useConfig();
  const { hasOverride, termsLabel } = useTermsAndConditionsLabel();
  const [currentIndex, setCurrentIndex] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const payload = documents.map((document) => ({
        documentId: document.documentId,
        versionId: document.versionId,
        language: document.language,
      }));
      const response = await axios.post<{ accessToken: string }>(
        '/api/v1/user-terms-acceptance',
        payload,
        {
          headers: {
            authorization: `Bearer ${termsToken}`,
          },
        },
      );
      onSuccess(response.data.accessToken);
    },
    onError: () => {
      enqueueSnackbar(t('common:common.hint.issue'), { variant: 'error' });
    },
  });

  const currentDocument = documents[currentIndex];
  if (!currentDocument) {
    throw new Error('Missing document');
  }

  const nDocuments = documents.length;
  const isFirstDocument = currentIndex === 0;
  const isLastDocument = currentIndex === nDocuments - 1;

  const signedUrlQuery = useQuery({
    queryKey: ['terms-and-conditions-document-signed-url', currentDocument.documentId, currentDocument.versionId, currentDocument.language, termsToken],
    queryFn: async () => {
      const response = await axios.post<SignedDocumentUrlResponse>(
        '/api/v1/signed-document-url/terms-and-conditions',
        {
          documentId: currentDocument.documentId,
          versionId: currentDocument.versionId,
          language: currentDocument.language,
        },
        {
          headers: {
            authorization: `Bearer ${termsToken}`,
          },
        });
      return response.data.signedUrl;
    },
    gcTime: 0,
  });

  const goBack = (): void => {
    setCurrentIndex((currentIndex) => currentIndex - 1);
  };

  const goForward = (): void => {
    if (isLastDocument) {
      acceptMutation.mutate();
      return;
    }
    setCurrentIndex((currentIndex) => currentIndex + 1);
  };

  return (
    <Container maxWidth="xl" sx={{ height: '100%' }}>
      <Paper sx={{ height: '100%', p: 4, overflowY: 'auto' }}>
        <Stack spacing={4} height="100%">
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Stack spacing={2} alignItems="center" textAlign="center">
              <Logo portal={portal} />
              <Typography>
                {hasOverride
                  ? t('auth:page.terms-conditions.agree-to-terms-v2.label', { productName: config.productName, termsLabel })
                  : t('auth:page.terms-conditions.agree-to-terms.label', { productName: config.productName })}
              </Typography>
            </Stack>
          </Box>
          <DocumentIframe
            signedUrlQuery={signedUrlQuery}
          />
          <Stack spacing={1} alignItems="flex-end">
            <Stack direction="row" spacing={1}>
              {!isFirstDocument && (
                <Button
                  disabled={acceptMutation.isPending}
                  onClick={goBack}
                >
                  {t('auth:page.terms-conditions.back.action')}
                </Button>
              )}
              <Button
                color="error"
                disabled={acceptMutation.isPending}
                onClick={onDecline}
              >
                {t('auth:page.terms-conditions.decline.action')}
              </Button>
              <LoadingButton
                variant="contained"
                isLoading={acceptMutation.isPending}
                loadingCaption={t('common:common.labels.loading')}
                onClick={goForward}
                disabled={signedUrlQuery.status !== 'success'}
              >
                {t('auth:page.terms-conditions.agree.action')}
              </LoadingButton>
            </Stack>
            <Typography>
              {t('auth:page.terms-conditions.count-completed.label', { completed: currentIndex, total: nDocuments })}
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    </Container>
  );
};

interface SuccessProps {
  accessToken: string;
  portal: string;
}

const Success: FC<SuccessProps> = ({ accessToken, portal }) => {
  const { t } = useTranslation(['auth']);
  const { hasOverride, termsLabel } = useTermsAndConditionsLabel();
  const config = useConfig();
  const auth = useAuth();
  const counter = useCountdown({
    seconds: 3,
    onEnd: () => {
      auth.setAccessToken(accessToken);
    },
  });

  return (
    <CenteredCard portal={portal}>
      <Typography>{hasOverride
        ? t('auth:page.terms-conditions.agreed.line-1-v2.label', { termsLabel })
        : t('auth:page.terms-conditions.agreed.line-1.label')}</Typography>
      <Typography>{t('auth:page.terms-conditions.agreed.line-2.label', { productName: config.productName })}</Typography>
      <Typography color='textPrimary'>
        {t('auth:page.terms-conditions.redirecting-you.label', { counter })}
      </Typography>
    </CenteredCard>
  );
};

const Declined: FC<{ portal: string }> = ({ portal }) => {
  const { t } = useTranslation(['auth']);
  const { hasOverride, termsLabel } = useTermsAndConditionsLabel();
  const { declineTermsAndConditions } = useAuth();
  const config = useConfig();

  return (
    <CenteredCard portal={portal}>
      <Typography>{t('auth:page.terms-conditions.declined.line-1.label')}</Typography>
      <Typography>{hasOverride
        ? t('auth:page.terms-conditions.declined.line-2-v2.label', { productName: config.productName, termsLabel })
        : t('page.terms-conditions.declined.line-2.label', { productName: config.productName })}</Typography>
      <Button onClick={() => { declineTermsAndConditions(); }}>
        {t('auth:page.terms-conditions.return-to-sign-in.action')}
      </Button>
    </CenteredCard>
  );
};

const CenteredCard: FC<PropsWithChildren & { portal: string }> = ({ children, portal }) => {
  return (
    <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <Logo portal={portal} />
            <Stack py={4} spacing={2} textAlign="center">
              {children}
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
};

const Logo: FC<{ portal: string }> = ({ portal }) => {
  const config = useConfig();
  const theme = useTheme();
  const isSmallScreen = useSmallScreen();

  const width = isSmallScreen ? '150px' : '250px';
  const logoImage = config.portals?.[portal]?.landing?.logoImage;

  return logoImage ? (
    <img
      src={logoImage}
      alt={config.productName ? `${config.productName} logo` : 'logo'}
      width={width}
    />
  ) : (
    <EthingsLogo logo={theme.landingPage.logo} width={width} />
  );
};

interface DocumentIframeProps {
  signedUrlQuery: UseQueryResult<string, Error>;
}

const DocumentIframe: FC<DocumentIframeProps> = ({ signedUrlQuery }) => {
  const { t } = useTranslation(['common']);

  if (signedUrlQuery.status === 'pending') {
    return <MiddleSpinner />;
  }

  if (signedUrlQuery.status === 'error') {
    return (
      <Box sx={{ width: '100%', height: '100%' }}>
        <Alert severity='error'>
          {t('common:page.errors.system-error.title')}
        </Alert>
      </Box>
    );
  }

  if (signedUrlQuery) {
    return (
      <Box
        component="iframe"
        src={signedUrlQuery.data}
        sx={{ border: 'none', width: '100%', height: '100%' }}
      />
    );
  }
};

const ErrorAlert: FC<{ portal: string }> = ({ portal }) => {
  const { t } = useTranslation(['common']);

  return (
    <CenteredCard portal={portal}>
      <Alert severity='error'>
        {t('common:page.errors.system-error.title')}
      </Alert>
    </CenteredCard>
  );
};
